import { Box } from '@material-ui/core';
import React, { ReactElement, useEffect, useMemo, useState } from 'react';
import { Loader } from '@googlemaps/js-api-loader';
import { config } from '../../../config/Config';
import { LatLng, LatLngBounds } from 'leaflet';

import './google-maps-viewer.styles.scss';
import { connect } from 'react-redux';
import {
	AppState,
	AppThunkDispatch,
	mapViewerSetZoom,
	MapViewerZoomAction
} from '../../../redux';
import { useMap } from 'react-leaflet';

export interface IGoogleMapsViewerProps {
	mapId: string;
	corner: LatLng;
	oppositeCorner: LatLng;
	hidden?: boolean;
}

export interface IGoogleMapsViewerStateProps {
	zoom?: number;
	setZoom: (mapId: string, zoom: number) => void;
}

export function GoogleMapsViewerView(
	props: IGoogleMapsViewerProps & IGoogleMapsViewerStateProps
): ReactElement {
	const { mapId, hidden, corner, oppositeCorner, zoom, setZoom } = props;
	const loader: Loader = new Loader({
		apiKey: config.googleMapsApiKey,
		language: 'nl'
	});

	const [map, setMap] = useState<google.maps.Map | undefined>(undefined);
	const mapReference = useMap();

	const mapBounds: LatLngBounds = useMemo(() => {
		return new LatLngBounds(corner, oppositeCorner);
	}, [corner, oppositeCorner]);

	useEffect(() => {
		const options: google.maps.MapOptions = {
			zoom: mapReference.getZoom(),
			fullscreenControl: true,
			zoomControl: true,
			streetViewControlOptions: {
				position: 5.0
			},
			fullscreenControlOptions: {
				position: 9.0
			}
		};
		loader.load().then(() => {
			const container: HTMLElement | null = document.getElementById(
				`googlemap-${mapId}`
			);
			if (!container) return;

			setMap(new google.maps.Map(container, options));
		});
	}, []);

	useEffect(() => {
		if (mapReference && !hidden) {
			mapReference.scrollWheelZoom.disable();
			const center: LatLng = mapReference.getCenter();
			if (map) {
				map.setCenter(center);
				if (zoom) map.setZoom(zoom);
			}
		}
		if (mapReference && hidden) {
			if (zoom && zoom > mapReference.getMaxZoom())
				setZoom(mapId, mapReference.getMaxZoom());
			if (zoom && zoom < mapReference.getMinZoom())
				setZoom(mapId, mapReference.getMinZoom());
			const center: google.maps.LatLng | undefined = map?.getCenter();
			if (center) {
				mapReference.setView(
					new LatLng(center.lat(), center.lng()),
					getMapZoom(),
					{ animate: false }
				);
			}
			mapReference.scrollWheelZoom.enable();
		}
	}, [hidden]);

	useEffect(() => {
		if (map) {
			map.fitBounds(toGoogleBounds(mapBounds), 1);

			map.getStreetView().addListener('visible_changed', () => {
				if (map.getStreetView().getVisible())
					mapReference.dragging.disable();
				else mapReference.dragging.enable();
			});
		}
	}, [map, mapReference]);

	function isLegalZoom(): boolean {
		if (map && typeof map.getZoom() === 'number') {
			const mapZoom: number = map.getZoom() as number;
			return (
				mapZoom < mapReference.getMaxZoom() &&
				mapZoom > mapReference.getMinZoom()
			);
		}
		return false;
	}

	function getMapZoom(): number {
		if (map) {
			const googleZoom: number = map.getZoom() as number;
			if (!isLegalZoom() && googleZoom < mapReference.getMinZoom())
				return mapReference.getMinZoom();
			if (!isLegalZoom() && googleZoom > mapReference.getMaxZoom())
				return mapReference.getMaxZoom();
			return googleZoom;
		}
		return mapReference.getZoom();
	}

	function toGoogleBounds(bounds: LatLngBounds): google.maps.LatLngBounds {
		const sw: google.maps.LatLng = new google.maps.LatLng({
			lat: bounds.getSouthWest().lat,
			lng: bounds.getSouthWest().lng
		});
		const ne: google.maps.LatLng = new google.maps.LatLng({
			lat: bounds.getNorthEast().lat,
			lng: bounds.getNorthEast().lng
		});
		return new google.maps.LatLngBounds(sw, ne);
	}

	return (
		<Box
			id={`googlemap-${mapId}`}
			className={`google-maps-viewer ${hidden ? 'hidden' : ''}`}
		></Box>
	);
}

function mapStateToProps(state: AppState, ownProps: IGoogleMapsViewerProps) {
	return {
		zoom: state.mapViewer.zoom.get(ownProps.mapId)
	};
}

function mapDispatchToProps(dispatch: AppThunkDispatch<MapViewerZoomAction>) {
	return {
		setZoom: (mapId: string, zoom: number) =>
			dispatch(mapViewerSetZoom(mapId, zoom))
	};
}

export const GoogleMapsViewer = connect(
	mapStateToProps,
	mapDispatchToProps
)(GoogleMapsViewerView);
